import { Avatar, IconButton, Stack, Typography, useTheme } from "@mui/material";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { IStoreState, useAppDispatch, useAppSelector } from "../../redux";
import moment from "moment";
import { CustomButton } from "../../Components/CustomButton";
import { ControlledCustomSelect } from "../../Components/formComponents";
import { CustomDatePicker } from "../../Components/formComponents/CustomDatePicker";
import PageContainer from "../../Components/PageContainer/PageContainer";
import { StandardTableActions } from "../../Components/StandardTableActions/StandardTableActions";
import { DataTable } from "../../Components/Table/DataTable";
import {
  IDataTableProps,
  MobileLogoRenderType,
  RenderType,
} from "../../Components/Table/interfaces/IDataTableProps";
import { defaultWaitlistEntry, fetchWaitListAsync, IWaitList, updateWaitlistStatusAsync, upsertWaitListAsync } from "../../redux/waitlist";
import { UpsertWaitlistDialog } from "./Dailogs/UpsertWaitList";
import { useOutletContext } from "../../providers";
import { CheckCircle } from "@mui/icons-material";
import { FaPencilAlt } from "react-icons/fa";
import { IQueryParams } from "../../common";
import { LoadState } from "../../constants/enum";

export const WaitList = () => {
  const navigate = useNavigate();
  const { outlet_details: { outlet_uuid, outlet_name } } = useOutletContext()

  const {
    data: waitList,
    totalRecords,
    loading,
  } = useAppSelector(
    (storeState: IStoreState) => storeState.waitlist.waitList
  );
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isLightMode = theme.palette.mode === "light";

  const [pagination, setPagination] = React.useState({
    pageNumber: 1,
    rowsPerPage: 10,
  });
  const [openUpsertDialog, setOpenUpsertDialog] =
    React.useState<IWaitList | null>(null);
  const [dateFilter, setDateFilter] = React.useState<string>("ALL");
  const [selectedStatus, setSelectedStatus] = React.useState<string>("ALL");
  const [isWaitingDoneFilter, setIsWaitingDoneFilter] = React.useState<string>("0");
  const [customDate, setCustomDate] = React.useState<string | null>(null);
  const [customDateDialog, setCustomDateDialog] =
    React.useState<boolean>(false);

  const handleWaitingDone = (row: IWaitList) => {
    dispatch(upsertWaitListAsync({
      ...row,
      is_waiting_done: 1,
    })).then(() => {
      dispatch(fetchWaitListAsync({
        queryParams: {
          page: pagination.pageNumber,
          rowsPerPage: pagination.rowsPerPage,
        },
        is_waiting_done: isWaitingDoneFilter
      }));
    });
  };

  React.useEffect(() => {
    const queryParams: IQueryParams = {
      page: pagination.pageNumber,
      rowsPerPage: pagination.rowsPerPage,
    };

    console.log("Sending query params:", queryParams); // Debug payload

    // Date filters
    if (dateFilter !== "ALL") {
      if (dateFilter === "CUSTOM" && customDate) {
        queryParams.fromDate = customDate;
        queryParams.toDate = customDate;
      } else if (dateFilter !== "CUSTOM") {
        queryParams.fromDate = dateFilter;
        queryParams.toDate = dateFilter;
      }
    }
    dispatch(fetchWaitListAsync({ queryParams, is_waiting_done: isWaitingDoneFilter }));

    const interval = setInterval(() => {
      dispatch(fetchWaitListAsync({ queryParams, is_waiting_done: isWaitingDoneFilter }));
    }, 60000)

    return () => clearInterval(interval); // Cleanup function to prevent memory leaks
  }, [pagination, dateFilter, customDate, selectedStatus, isWaitingDoneFilter, dispatch]);

  const dataTableProps: IDataTableProps = {
    isPagination: true,
    totalRecords: totalRecords,
    rowsPerPageOptions: pagination.rowsPerPage,
    isDataLoading: loading !== LoadState.succeeded,
    mobileLogo: {
      type: MobileLogoRenderType.reactNode,
      onMobileLogoRender: (data: any) => {
        return (
          <Avatar
            sx={{
              width: 45,
              height: 45,
              color: "#fff",
              ml: "-8px",
              mr: 2,
            }}
          ></Avatar>
        );
      },
    },
    columns: [
      {
        key: "view",
        headerName: "Actions",
        fieldName: "",
        headingAlign: "center",
        renderType: RenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IWaitList) => {
          return (
            <Stack direction="row" spacing={1}>
              <IconButton
                color={theme.palette.mode === "dark" ? "secondary" : "primary"}
                onClick={() => setOpenUpsertDialog(row)}
              >
                <FaPencilAlt />
              </IconButton>
              <IconButton
                color="success"
                onClick={() => handleWaitingDone(row)}
              >
                <CheckCircle />
              </IconButton>
            </Stack>
          );
        },
      },
      // {
      //   key: "is_waiting_done",
      //   headerName: " is_waiting_done",
      //   fieldName: "is_waiting_done",
      //   enableSorting: true,
      //   renderType: RenderType.CUSTOM_RENDER,
      //   onRowCellRender: (value, row: IWaitList) => {
      //     return (
      //       <Typography variant="body1" textAlign={"left"}>
      //         {row.is_waiting_done}
      //       </Typography>
      //     );
      //   },
      // },
      {
        key: "customer_name",
        headerName: " Name",
        fieldName: "customer_name",
        enableSorting: true,
        renderType: RenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IWaitList) => {
          return (
            <Typography variant="body1" textAlign={"left"}>
              {row.customer_name}
            </Typography>
          );
        },
      },
      {
        key: "priority",
        headerName: "Priority",
        fieldName: "priority",
        enableSorting: true,
        renderType: RenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IWaitList) => {
          return (
            <Typography variant="body1" textAlign={"left"}>
              {row.priority}
            </Typography>
          );
        },
      },
      {
        key: "number_of_guests",
        headerName: "Guests",
        fieldName: "number_of_guests",
        enableSorting: true,
        renderType: RenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IWaitList) => {
          return (
            <Typography variant="body1" textAlign={"left"}>
              {row.number_of_guests}
            </Typography>
          );
        },
      },
      {
        key: "customer_mobile_no",
        headerName: "Phone",
        fieldName: "customer_mobile_no",
        enableSorting: true,
        renderType: RenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IWaitList) => {
          return (
            <Typography variant="body1" textAlign={"left"}>
              {row.customer_mobile_no}
            </Typography>
          );
        },
      },
      {
        key: "is_waiting_done",
        headerName: "Waiting Status",
        fieldName: "is_waiting_done",
        enableSorting: true,
        renderType: RenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IWaitList) => {
          return (
            <Typography
              variant="body1"
              color={row.is_waiting_done ? "success.main" : "warning.main"}
              textAlign={"left"}
            >
              {row.is_waiting_done ? "Done" : "Waiting"}
            </Typography>
          );
        },
      },
      {
        key: "waiting_time",
        headerName: "Waiting Time",
        fieldName: "waiting_time",
        enableSorting: true,
        renderType: RenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IWaitList) => {
          return (
            <Typography variant="body1" textAlign={"left"}>
              {row.waiting_time}
            </Typography>
          );
        },
      },
    ],
    items: waitList,
    onPageChange: (newPageNumber: number) => {
      setPagination({ ...pagination, pageNumber: newPageNumber });
    },
    onRowsPerPageChange: (pageNumber: number, rowsPerPage: number) => {
      setPagination({
        pageNumber: pageNumber,
        rowsPerPage: rowsPerPage,
      });
    },
  };

  return (
    <PageContainer title="WaitList" description="inner page">
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        my={2}
      >
        <Stack
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          spacing={2}
        >
          <Typography variant="h2" color={theme.palette.mode === "dark" ? "white" : "black"}>Waitlist</Typography>

          <ControlledCustomSelect
            label="Filter by Date"
            options={[
              { label: "All", value: "ALL" },
              { label: "Today", value: moment().format("yyyy-MM-DD") },
              {
                label: "Yesterday",
                value: moment().subtract(1, "day").format("yyyy-MM-DD"),
              },
              { label: "Custom", value: "CUSTOM" },
            ]}
            value={dateFilter}
            onChange={(evt) => setDateFilter(evt.target.value as string)}
            sx={{ minWidth: 120 }}
            fullWidth
            size="small"
          />

          {customDateDialog && (
            <CustomDatePicker
              label="Select Date"
              fullWidth
              fieldName="reservation_date"
              value={customDate || ""}
              onChange={(newValue, formattedDate) => {
                setCustomDate(formattedDate);
              }}
            />
          )}

          <ControlledCustomSelect
            label="Waiting Status"
            options={[
              { label: "All", value: "-1" },
              { label: "Waiting Done", value: "1" },
              { label: "Waiting", value: "0" },
            ]}
            value={String(isWaitingDoneFilter)}
            onChange={(evt) => setIsWaitingDoneFilter(evt.target.value as string)}
            sx={{ minWidth: 120 }}
            fullWidth
            size="small"
          />

        </Stack>

        <CustomButton
          variant="outlined"
          sx={{
            backgroundColor: isLightMode ? "var(--secondary-color)" : "var(--primary-color)",
            color: isLightMode ? "var(--text-light-color)" : "var(--text-dark-color)",
            border: isLightMode ? "var(--border-light-color) 1px solid" : "var(--border-dark-color) 1px solid",

            "&:hover": {
              backgroundColor: isLightMode ? "var(--secondary-color)" : "var(--tertiary-color)",
              opacity: isLightMode ? 0.85 : 1,
              // color: isLightMode ? "var(--primary-color)" : "var(--secondary-color)",
              border: isLightMode ? "var(--border-light-color) 1px solid" : "var(--border-dark-color) 1px solid",
            }
          }}
          onClick={() => {
            if (outlet_uuid && outlet_name) {
              setOpenUpsertDialog({ ...defaultWaitlistEntry, outlet_uuid, outlet_name });
            }
          }}
        >
          Add Waitlist
        </CustomButton>
      </Stack>

      <DataTable {...dataTableProps} />

      {openUpsertDialog && (
        <UpsertWaitlistDialog
          open={true}
          waitlist={openUpsertDialog}
          onClose={() => setOpenUpsertDialog(null)}
          onSuccess={() => {
            dispatch(
              fetchWaitListAsync({
                queryParams: {
                  page: pagination.pageNumber,
                  rowsPerPage: pagination.rowsPerPage,
                },
                is_waiting_done: isWaitingDoneFilter
              })
            );
          }}
        />
      )}
    </PageContainer>
  );
};